import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, bildLaden, dateiSenden, fehlertext } from "./api";
import { ablageAendern, holenMitAblage } from "./ablage";
import { AblageHinweis } from "./netzstand";
import { sendenOderEinreihen } from "./warteschlange";
import { objektAuswahl } from "./objektname";
import {
  Auswahl, Eingabe, Feld, Fehler, Karte, Knopf, Laedt, Leer, Seitenkopf, Textfeld
} from "./ui";

/**
 * Arbeitsauftraege im Backoffice.
 *
 * Die Liste beantwortet eine Frage: was ist heute zu tun. Deshalb steht sie
 * standardmaessig auf "offen", sortiert nach Zustand und Termin, und nicht
 * chronologisch. Ein Auftrag von gestern, der laeuft, ist wichtiger als einer
 * von heute, den noch niemand angefasst hat.
 */

export interface Auftrag {
  id: string;
  number: number;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  due_date: string | null;
  source: string;
  created_at: string;
  closed_at: string | null;
  property_id: string;
  property_name: string;
  customer_name: string;
  assigned_to: string | null;
  assigned_name: string | null;
  fotos?: number;
}

interface Ereignis {
  id: string;
  type: string;
  note: string | null;
  created_at: string;
  actor_name: string | null;
}

interface Auftragsfoto {
  id: string;
  media_id: string;
  thumb_id: string | null;
  kind: string;
  caption: string | null;
}

const ZUSTAENDE = ["open", "scheduled", "in_progress", "waiting", "done", "cancelled"] as const;
const DRINGLICHKEITEN = ["low", "normal", "high", "urgent"] as const;

// --------------------------------------------------------------------- Liste

export function AuftraegeListe() {
  const { t, i18n } = useTranslation();
  const [liste, setListe] = useState<Auftrag[] | null>(null);
  const [status, setStatus] = useState("offen");
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    const p = status === "offen" ? "?offen=ja" : status ? `?status=${status}` : "";
    void api.get<{ workOrders: Auftrag[] }>(`/work-orders${p}`)
      .then((r) => { setListe(r.workOrders); setFehler(null); })
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [status, t]);

  return (
    <>
      <Seitenkopf
        titel={t("auftraege.titel")}
        hinweis={liste ? t("auftraege.anzahl", { count: liste.length }) : undefined}
        aktionen={<Link to="/auftraege/neu"><Knopf>{t("auftraege.neu")}</Knopf></Link>}
      />
      <div className="mb-4 max-w-xs">
        <Auswahl value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t("auftraege.zustand")}>
          <option value="offen">{t("auftraege.offen")}</option>
          <option value="">{t("auftraege.alle")}</option>
          {ZUSTAENDE.map((z) => (
            <option key={z} value={z}>{t(`auftraege.status.${z}`)}</option>
          ))}
        </Auswahl>
      </div>
      <Fehler text={fehler} />

      {!liste ? <Laedt /> : liste.length === 0 ? <Leer text={t("auftraege.leer")} /> : (
        <ul className="space-y-3">
          {liste.map((a) => (
            <li key={a.id}>
              <Link to={`/auftraege/${a.id}`} className="block">
                <Karte className="transition hover:shadow-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{a.title}</span>
                    <Zustandsmarke status={a.status} text={t(`auftraege.status.${a.status}`)} />
                    {a.priority !== "normal" && (
                      <Dringlichkeit art={a.priority} text={t(`auftraege.prio.${a.priority}`)} />
                    )}
                    <span className="ms-auto text-sm text-leise">
                      {t("auftraege.nummer", { nummer: a.number })}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-leise">
                    {a.customer_name} · {a.property_name}
                    {a.assigned_name ? ` · ${a.assigned_name}` : ` · ${t("auftraege.ohneZuweisung")}`}
                    {a.due_date ? ` · ${new Date(a.due_date).toLocaleDateString(i18n.language)}` : ""}
                  </div>
                </Karte>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// --------------------------------------------------------------------- Neu

export function AuftragNeu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [objekte, setObjekte] = useState<{ id: string; name: string; customer_name: string }[]>([]);
  const [werte, setWerte] = useState({
    propertyId: "", title: "", description: "", priority: "normal", dueDate: ""
  });
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    void api.get<{ properties: { id: string; name: string; customer_name: string }[] }>("/properties")
      .then((r) => setObjekte(r.properties)).catch(() => undefined);
  }, []);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setLaeuft(true);
    setFehler(null);
    try {
      const a = await api.post<{ id: string }>("/work-orders", {
        ...werte, dueDate: werte.dueDate || null
      });
      navigate(`/auftraege/${a.id}`);
    } catch (err) {
      setFehler(fehlertext(err, t("fehler.speichern")));
      setLaeuft(false);
    }
  }

  return (
    <form onSubmit={absenden} className="max-w-xl">
      <Seitenkopf titel={t("auftraege.neu")} />
      <Fehler text={fehler} />
      <Karte className="space-y-4">
        <Feld label={t("auftraege.objekt")}>
          <Auswahl required value={werte.propertyId}
            onChange={(e) => setWerte({ ...werte, propertyId: e.target.value })}>
            <option value="">{t("objekte.kundeWaehlen")}</option>
            {objektAuswahl(objekte).map(({ objekt, beschriftung }) => (
              <option key={objekt.id} value={objekt.id}>{beschriftung}</option>
            ))}
          </Auswahl>
        </Feld>
        <Feld label={t("auftraege.titel1")}>
          <Eingabe required value={werte.title}
            onChange={(e) => setWerte({ ...werte, title: e.target.value })} />
        </Feld>
        <Feld label={t("auftraege.beschreibung")}>
          <Textfeld rows={4} value={werte.description}
            onChange={(e) => setWerte({ ...werte, description: e.target.value })} />
        </Feld>
        <Feld label={t("auftraege.prioritaet")}>
          <Auswahl value={werte.priority}
            onChange={(e) => setWerte({ ...werte, priority: e.target.value })}>
            {DRINGLICHKEITEN.map((d) => (
              <option key={d} value={d}>{t(`auftraege.prio.${d}`)}</option>
            ))}
          </Auswahl>
        </Feld>
        <Feld label={t("auftraege.termin")}>
          <Eingabe type="date" value={werte.dueDate}
            onChange={(e) => setWerte({ ...werte, dueDate: e.target.value })} />
        </Feld>
      </Karte>
      <div className="mt-4">
        <Knopf type="submit" disabled={laeuft}>
          {laeuft ? t("aktion.speichert") : t("aktion.speichern")}
        </Knopf>
      </div>
    </form>
  );
}

// ------------------------------------------------------------------- Detail

export function AuftragSeite() {
  const { t, i18n } = useTranslation();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [daten, setDaten] = useState<
    { workOrder: Auftrag & { assigned_to: string | null }; verlauf: Ereignis[]; fotos: Auftragsfoto[] } | null
  >(null);
  const [mitarbeiter, setMitarbeiter] = useState<{ id: string; display_name: string }[]>([]);
  const [notiz, setNotiz] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [ablageStand, setAblageStand] = useState<string | null>(null);
  /** Ohne Netz gespeichert: ein ruhiger Hinweis, kein Fehler. */
  const [gemerkt, setGemerkt] = useState(false);

  async function loeschen() {
    if (!window.confirm(t("auftraege.loeschenSicher"))) return;
    setLaeuft(true);
    setFehler(null);
    try {
      const antwort = await api.del<{ geloesteZeiten: number }>(`/work-orders/${id}`);
      /*
       * Gebuchte Zeiten verschwinden nicht mit dem Auftrag, sie verlieren nur
       * den Verweis. Wenn das jemanden betrifft, soll er es hier erfahren und
       * nicht erst beim Monatsabschluss.
       */
      if (antwort.geloesteZeiten > 0) {
        window.alert(t("auftraege.geloesteZeiten", { count: antwort.geloesteZeiten }));
      }
      navigate("/auftraege");
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
      setLaeuft(false);
    }
  }

  function laden() {
    // Auch der Auftrag kommt ohne Netz aus der Kopie auf dem Geraet. Wer am
    // Objekt zurueckmelden soll, muss zuerst lesen koennen, was zu tun war.
    void holenMitAblage<NonNullable<typeof daten>>(`/work-orders/${id}`)
      .then(({ daten: geladen, stand }) => { setDaten(geladen); setAblageStand(stand); })
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }
  useEffect(laden, [id]);

  useEffect(() => {
    void api.get<{ users: { id: string; display_name: string }[] }>("/auth/users")
      .then((r) => setMitarbeiter(r.users)).catch(() => undefined);
  }, []);

  async function aendern(feld: Record<string, unknown>) {
    setFehler(null);
    setGemerkt(false);
    try {
      const art = await sendenOderEinreihen(
        {
          art: "auftrag",
          pfad: `/work-orders/${id}`,
          methode: "PATCH",
          koerper: feld,
          beschreibung: t("auftraege.zustand")
        },
        async () => { await api.patch(`/work-orders/${id}`, feld); }
      );
      if (art === "eingereiht") {
        setGemerkt(true);
        setDaten((alt) => (alt ? { ...alt, workOrder: { ...alt.workOrder, ...feld } } : alt));
        await ablageAendern<NonNullable<typeof daten>>(`/work-orders/${id}`, (a) => ({
          ...a, workOrder: { ...a.workOrder, ...feld }
        }));
      } else {
        laden();
      }
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  /**
   * Die Rueckmeldung vom Objekt: Notiz und Foto.
   *
   * Beides muss ohne Netz gehen. Ein Auftrag wird dort erledigt, wo es oft
   * keinen Empfang gibt, und die Rueckmeldung ist der Teil, der spaeter im
   * Bericht steht.
   */
  async function notizSenden(e: FormEvent) {
    e.preventDefault();
    if (!notiz.trim()) return;
    setLaeuft(true);
    setGemerkt(false);
    try {
      const text = notiz;
      const art = await sendenOderEinreihen(
        {
          art: "auftrag",
          pfad: `/work-orders/${id}/notes`,
          methode: "POST",
          koerper: { note: text },
          beschreibung: t("auftraege.notiz")
        },
        async () => { await api.post(`/work-orders/${id}/notes`, { note: text }); }
      );
      setNotiz("");
      if (art === "eingereiht") setGemerkt(true);
      else laden();
    } catch (err) {
      setFehler(fehlertext(err, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  async function fotoSenden(datei: File, art: string) {
    setFehler(null);
    setGemerkt(false);
    try {
      const wie = await sendenOderEinreihen(
        {
          art: "auftrag",
          pfad: `/work-orders/${id}/photos`,
          methode: "POST",
          datei: { blob: datei, name: datei.name || "foto.jpg", feldname: "file", felder: { kind: art } },
          beschreibung: t("auftraege.fotos")
        },
        async () => {
          const form = new FormData();
          form.append("file", datei);
          form.append("kind", art);
          await dateiSenden(`/work-orders/${id}/photos`, form);
          laden();
        }
      );
      if (wie === "eingereiht") setGemerkt(true);
    } catch (err) {
      setFehler(fehlertext(err, t("fehler.speichern")));
    }
  }

  if (!daten) return <Laedt />;
  const a = daten.workOrder;

  return (
    <>
      <Link to="/auftraege" className="text-sm text-leise underline">{t("auftraege.zurueckZurListe")}</Link>
      <Seitenkopf
        titel={a.title}
        hinweis={`${a.customer_name} · ${a.property_name} · ${t("auftraege.nummer", { nummer: a.number })}`}
        aktionen={
          /*
           * Geloescht wird nur von der Geschaeftsleitung; entschieden wird das
           * in der API. Eine Sperre nach Herkunft gibt es nicht mehr, seit
           * jeder Auftrag im Haus entsteht.
           */
          <Knopf art="warnung" disabled={laeuft} onClick={() => void loeschen()}>
            {t("aktion.loeschen")}
          </Knopf>
        }
      />
      <Fehler text={fehler} />
      <AblageHinweis stand={ablageStand} />
      {gemerkt && (
        <p className="mb-4 rounded border border-marke bg-marke/10 px-3 py-2 text-sm">
          {t("netz.gemerkt")}
        </p>
      )}

      <Karte className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Feld label={t("auftraege.zustand")}>
            <Auswahl value={a.status} onChange={(e) => void aendern({ status: e.target.value })}>
              {ZUSTAENDE.map((z) => (
                <option key={z} value={z}>{t(`auftraege.status.${z}`)}</option>
              ))}
            </Auswahl>
          </Feld>
          <Feld label={t("auftraege.prioritaet")}>
            <Auswahl value={a.priority} onChange={(e) => void aendern({ priority: e.target.value })}>
              {DRINGLICHKEITEN.map((d) => (
                <option key={d} value={d}>{t(`auftraege.prio.${d}`)}</option>
              ))}
            </Auswahl>
          </Feld>
          <Feld label={t("auftraege.zugewiesen")}>
            <Auswahl value={a.assigned_to ?? ""}
              onChange={(e) => void aendern({ assignedTo: e.target.value || null })}>
              <option value="">{t("auftraege.ohneZuweisung")}</option>
              {mitarbeiter.map((m) => (
                <option key={m.id} value={m.id}>{m.display_name}</option>
              ))}
            </Auswahl>
          </Feld>
          <Feld label={t("auftraege.termin")}>
            <Eingabe type="date" value={a.due_date?.slice(0, 10) ?? ""}
              onChange={(e) => void aendern({ dueDate: e.target.value || null })} />
          </Feld>
        </div>

        {a.description && <p className="whitespace-pre-line text-sm">{a.description}</p>}

      </Karte>

      {/*
        Vorher und Nachher. Das Nachherbild beantwortet die Frage, ob wirklich
        etwas passiert ist, besser als jeder Zustandswechsel, und es ist das,
        was im Bericht an den Eigentuemer landet.
      */}
      <h2 className="mt-8 text-xl font-bold">{t("auftraege.fotos")}</h2>
      <Karte className="mt-3">
        <div className="flex flex-wrap gap-3">
          {daten.fotos.map((f) => (
            <figure key={f.id}>
              <Auftragsbild mediaId={f.thumb_id ?? f.media_id} />
              <figcaption className="mt-1 text-xs text-leise">
                {t(`auftraege.fotoart.${f.kind}`)}
              </figcaption>
            </figure>
          ))}
          {daten.fotos.length === 0 && (
            <p className="text-sm text-leise">{t("auftraege.keineFotos")}</p>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-4">
          {(["before", "after", "receipt"] as const).map((art) => (
            <label key={art} className="cursor-pointer text-sm font-semibold text-verweis underline">
              {t(`auftraege.fotoHinzu.${art}`)}
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => {
                  const d = e.target.files?.[0];
                  if (d) void fotoSenden(d, art);
                  e.target.value = "";
                }} />
            </label>
          ))}
        </div>
      </Karte>

      <h2 className="mt-8 text-xl font-bold">{t("auftraege.verlauf")}</h2>
      <Karte className="mt-3">
        <ul className="divide-y divide-linie">
          {daten.verlauf.map((e) => (
            <li key={e.id} className="py-2 text-sm">
              <span className="text-leise">
                {new Date(e.created_at).toLocaleString(i18n.language)}
                {e.actor_name ? ` · ${e.actor_name}` : ""} ·{" "}
              </span>
              {t(`auftraege.ereignis.${e.type}`, { defaultValue: e.type })}
              {e.note && <div className="mt-1 whitespace-pre-line">{e.note}</div>}
            </li>
          ))}
        </ul>

        <form onSubmit={notizSenden} className="mt-4 flex flex-wrap gap-2">
          <Eingabe className="flex-1" value={notiz} placeholder={t("auftraege.notizPlatzhalter")}
            onChange={(e) => setNotiz(e.target.value)} />
          <Knopf type="submit" disabled={laeuft || !notiz.trim()}>{t("auftraege.notiz")}</Knopf>
        </form>
      </Karte>
    </>
  );
}

// ------------------------------------------------------------------- Teile

function Zustandsmarke({ status, text }: { status: string; text: string }) {
  const farbe = status === "done"
    ? "bg-green-700 text-white"
    : status === "cancelled"
      ? "bg-leise text-white"
      : status === "open"
        ? "bg-grund text-tinte"
        : "bg-marke text-white";
  return <span className={`rounded px-2 py-0.5 text-xs font-semibold ${farbe}`}>{text}</span>;
}

function Dringlichkeit({ art, text }: { art: string; text: string }) {
  const farbe = art === "urgent"
    ? "bg-red-700 text-white"
    : art === "high"
      ? "bg-red-100 text-red-800"
      : "bg-grund text-leise";
  return <span className={`rounded px-2 py-0.5 text-xs font-semibold ${farbe}`}>{text}</span>;
}

function Auftragsbild({ mediaId }: { mediaId: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let lebt = true;
    let erzeugt: string | null = null;
    void bildLaden(`/work-order-media/${mediaId}`).then((u) => {
      if (!lebt) { URL.revokeObjectURL(u); return; }
      erzeugt = u;
      setUrl(u);
    }).catch(() => undefined);
    return () => { lebt = false; if (erzeugt) URL.revokeObjectURL(erzeugt); };
  }, [mediaId]);

  if (!url) return <div className="h-24 w-24 animate-pulse rounded bg-grund" />;
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt="" className="h-24 w-24 rounded object-cover" />
    </a>
  );
}
